import { Navigate, Outlet, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';

import { ArticleVisibility } from 'pages/ArticleVisibility';
import { InsightsPro } from 'pages/insights-pro/InsightsPro';
import { MarketplaceDashboard } from 'pages/insights-pro/marketplace-tab/MarketplaceDashboard';
import { CountryRoutes } from 'pages/insights-pro/country-tab/CountryRoutes';
import { Benchmarking } from 'pages/Benchmarking/Benchmarking';
import { PerformanceDashboard as BenchmarkingPerformanceDashboard } from 'pages/Benchmarking/performance/PerformanceDashboard';
import { MarketplacesDashboard as BenchmarkMarketplacesDashboard } from 'pages/Benchmarking/marketplaces/MarketplacesDashboard';
import { CountriesDashboard as BenchmarkCountriesDashboard } from 'pages/Benchmarking/countries/CountriesDashboard';
import { CategoriesDashboard as BenchmarkCategoriesDashboard } from 'pages/Benchmarking/categories/CategoriesDashboard';
import { ProductRoutes } from 'pages/insights-pro/product-tab/ProductRoutes';
import { MarketplaceRoutes } from 'pages/insights-pro/marketplace-tab/MarketplaceRoutes';
import { useContext, useEffect, useMemo } from 'react';
import { BENCHMARKING, TenantConfigContext } from 'context/TenantConfigContext';
import { CategoryRoutes } from 'pages/insights-pro/category-tab/CategoryRoutes';
import { BenchmarkingTeaser } from 'pages/BenchmarkingTeaser/BenchmarkingTeaser';
import { ApiAccess } from 'pages/api-access/ApiAccess';
import { ApiAccessAbout } from 'pages/api-access/ApiAccessAbout';
import { ApiAccessToken } from 'pages/api-access/ApiAccessToken';
import { ApiAccessDocumentation } from 'pages/api-access/ApiAccessDocumentation';
import { UserTokensView } from 'pages/api-access/UserTokensView';
import { PerformanceRoutes } from 'pages/insights-pro/performance-tab/PerformanceRoutes';
import { useAppStore } from 'hooks/state/useAppStore';
import {
  useAgeGroupDataService,
  useBrandDataService,
  useCategoryDataService,
  useCountryDataService,
  useFulfillmentMethodsDataService,
  useGenderDataService,
  useMarketplacesDataService,
  useSeasonDataService,
} from 'services/rest-api.service';
import {
  getMatomoModuleKeyFromPathname,
  getMatomoPageTitle,
  MATOMO_CUSTOM_DIMENSION_MODULE,
  setMatomoCustomDimension,
  trackMatomoPageView,
} from 'services/matomo.service';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { Trading } from 'pages/trading/Trading';
import { TradingMessages } from 'pages/trading/TradingMessages';
import { DrillDownPage } from 'pages/trading/outlier/DrillDownPage';
import { OutlierDetailPage } from 'pages/trading/outlier/OutlierDetailPage';
import { MarketShareDetailPage } from 'pages/trading/marketshare/MarketShareDetailPage';
import { BenchmarkingDrillDown } from 'pages/Benchmarking/drilldown/BenchmarkingDrillDown';
import { PriceDetailPage } from 'pages/trading/price/change/PriceDetailPage';
import { ReleaseNotes } from 'pages/release-notes/ReleaseNotes';
import { StyleDetailRouter } from 'pages/trading/price/StyleDetailRouter';
import { ProductEntityPage } from 'pages/entities/product/ProductEntityPage';
import { CategoryEntityPage } from 'pages/entities/category/CategoryEntityPage';
import { ArticleEntityPage } from 'pages/entities/article/ArticleEntityPage';
import { StyleEntityPage } from 'pages/entities/style/StyleEntityPage';
import { Profitability } from 'pages/Profitability';
import { OverviewPage } from 'pages/Profitability/OverviewPage';
import { ProfitabilityBreakdownPage } from 'pages/Profitability/breakdown/ProfitabilityBreakdownPage';
import { ProfitabilityDrillDownPage } from 'pages/Profitability/drilldown/ProfitabilityDrillDownPage';
import { CostConfigurationsPage } from 'pages/Profitability/costConfigurations/CostConfigurationsPage';

function TradingLegacyRedirect() {
  const location = useLocation();

  const newPath = location.pathname.replace(/^\/trading(\/|$)/, '/steeringhub/insightsfeed$1');

  return <Navigate to={`${newPath}${location.search}`} replace />;
}

export function AppRoutes() {
  const location = useLocation();
  const { accountNumber, permissions } = useContext(TenantConfigContext);
  const hasBenchmarkingPermission = useMemo(() => permissions.includes(BENCHMARKING), [permissions]);

  const insightsBasePath = '/insights';
  const legacyInsightsPath = location.pathname.replace(/^\/insights-pro(\/|$)/, `${insightsBasePath}$1`);

  useEffect(() => {
    const moduleKey = getMatomoModuleKeyFromPathname(location.pathname);
    if (!moduleKey) return;

    setMatomoCustomDimension(MATOMO_CUSTOM_DIMENSION_MODULE, moduleKey);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== window.location.pathname) return;

    const pageTitle = getMatomoPageTitle(window.location.pathname);
    trackMatomoPageView(window.location.href, pageTitle);
  }, [location.pathname, location.search]);

  const [, setSearchParams] = useSearchParams();

  const appStore = useAppStore();

  const transitiveCacheStore = useTransitiveCacheStore();

  useEffect(() => {
    // On back/forward navigation, Zustand stores still hold in-memory state
    // from the page we just left.  Re-read the URL search params so the
    // stores match the restored history entry.
    const handlePopstate = () => {
      useBreadcrumbStore.persist.rehydrate();
      useAppStore.persist.rehydrate();
    };
    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, []);

  useEffect(() => {
    // Skip during cross-route transitions when React Router location is stale
    if (location.pathname !== window.location.pathname) return;
    const realUrl = new URL(window.location.href).searchParams;
    setSearchParams(realUrl, { replace: true });
  }, [appStore, location.pathname]);

  const { getAll: getAllBrands } = useBrandDataService();
  const { getAll: getAllMarketplaces } = useMarketplacesDataService();
  const { getAll: getAllCountries } = useCountryDataService();
  const { getAll: getAllCategories } = useCategoryDataService();
  const { getAll: getAllFulfillmentMethods } = useFulfillmentMethodsDataService();
  const { getAll: getAllGenders } = useGenderDataService();
  const { getAll: getAllAgeGroups } = useAgeGroupDataService();
  const { getAll: getAllSeasons } = useSeasonDataService();

  useEffect(() => {
    if (accountNumber && permissions) {
      getAllBrands().then(response => transitiveCacheStore.setAllBrands(response.data?.brands ?? []));
      getAllMarketplaces().then(response => transitiveCacheStore.setAllMarketplaces(response.data?.marketplaces ?? []));
      getAllCountries().then(response => transitiveCacheStore.setAllCountries(response.data?.countries ?? []));
      getAllFulfillmentMethods().then(response =>
        transitiveCacheStore.setAllFulfillmentMethods(response.data?.fulfillmentMethods ?? [])
      );
      getAllCategories().then(response => transitiveCacheStore.setAllCategories(response.data?.categories ?? []));
      getAllGenders().then(response => transitiveCacheStore.setAllGenders(response.data?.genders ?? []));
      getAllAgeGroups().then(response => transitiveCacheStore.setAllAgeGroups(response.data?.ageGroups ?? []));
      getAllSeasons().then(response => transitiveCacheStore.setAllSeasons(response.data?.seasons ?? []));
    }
  }, [accountNumber, permissions]);

  return (
    <>
      <Outlet />
      {accountNumber && (
        <Routes>
          <Route path="/" element={<Navigate to={`${insightsBasePath}/performance${location.search}`} replace />} />
          <Route
            path="callback"
            element={<Navigate to={`${insightsBasePath}/performance${location.search}`} replace />}
          />
          <Route path="logout" element={<Navigate to="/" />} />
          <Route path="insights-pro/*" element={<Navigate to={`${legacyInsightsPath}${location.search}`} replace />} />
          <Route path="insights" element={<InsightsPro />}>
            <Route index element={<Navigate to={`performance${location.search}`} replace />} />
            <Route path="performance/*" element={<PerformanceRoutes />} />
            <Route path="marketplace" element={<MarketplaceDashboard />} />
            <Route path="marketplace/*" element={<MarketplaceRoutes />} />
            <Route path="category/*" element={<CategoryRoutes />} />
            <Route path="product/*" element={<ProductRoutes />} />
            <Route path="country/*" element={<CountryRoutes />} />
          </Route>
          <Route path={`product/:productNumber/:tab?`} element={<ProductEntityPage />} />
          <Route path={`article/:articleEAN/:tab?`} element={<ArticleEntityPage />} />
          <Route path={`category/:category/:tab?`} element={<CategoryEntityPage />} />
          <Route path={`style/:style/:tab?`} element={<StyleEntityPage />} />
          {hasBenchmarkingPermission && (
            <Route path="benchmarking" element={<Benchmarking />}>
              <Route index element={<Navigate to={`performance${location.search}`} replace />} />
              <Route path="performance" element={<BenchmarkingPerformanceDashboard />} />
              <Route path="marketplaces" element={<BenchmarkMarketplacesDashboard />} />
              <Route path="countries" element={<BenchmarkCountriesDashboard />} />
              <Route path="categories" element={<BenchmarkCategoriesDashboard />} />
              <Route path="drilldown/:attributeType/:attributeValue" element={<BenchmarkingDrillDown />} />
            </Route>
          )}
          {!hasBenchmarkingPermission && <Route path="benchmarking/*" element={<BenchmarkingTeaser />} />}
          <Route path="api" element={<ApiAccess />}>
            <Route index element={<Navigate to={`about${location.search}`} replace />} />
            <Route path="about" element={<ApiAccessAbout />} />
            <Route path="token" element={<ApiAccessToken />} />
            <Route path="token/token-management" element={<UserTokensView />} />
            <Route path="documentation" element={<ApiAccessDocumentation />} />
          </Route>
          <Route path="articlevisibility" element={<ArticleVisibility />} />
          <Route path="releasenotes" element={<ReleaseNotes />} />

          <Route path="profitability" element={<Profitability />}>
            <Route index element={<Navigate to={`overview${location.search}`} replace />} />
            <Route path="overview" element={<OverviewPage />} />
            <Route path=":attributeCode" element={<ProfitabilityBreakdownPage />} />
            <Route path="drilldown/:coreAttributeCode/:coreAttributeValue" element={<ProfitabilityDrillDownPage />} />
            <Route path="*" element={<Navigate to={`/profitability${location.search}`} replace />} />
            <Route path="cost-configurations" element={<CostConfigurationsPage />} />
          </Route>
          <Route path="steeringhub" element={<Outlet />}>
            <Route index element={<Navigate to={`insightsfeed${location.search}`} replace />} />
            <Route path="insightsfeed" element={<Trading />}>
              <Route index element={<TradingMessages />} />
              <Route path="messages" element={<Navigate to={`..${location.search}`} replace />} />
              <Route path="detail/:attributeCode/:messageId" element={<OutlierDetailPage />} />
              <Route path="marketshare/:messageId" element={<MarketShareDetailPage />} />
              <Route path="drilldown/:attributeType/:attributeValue" element={<DrillDownPage />} />
              <Route path="price-analysis/:messageId" element={<PriceDetailPage />} />
              <Route path="price-analysis/:messageId/style/:styleId" element={<StyleDetailRouter />} />
            </Route>
          </Route>

          {/* Legacy URLs */}
          <Route path="insightsfeed/*" element={<TradingLegacyRedirect />} />
          <Route path="*" element={<Navigate to={`${insightsBasePath}/performance${location.search}`} replace />} />
        </Routes>
      )}
    </>
  );
}
